import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useListaFlutuante } from '../hooks/useListaFlutuante'

interface Natureza {
  tno_cod: number
  tno_cdf: number
  tno_des: string
  tno_dsc: string
  tno_loc?: string
}

interface Props {
  naturezas: Natureza[]
  value: string
  onChange: (cod: string) => void
}

// tno_loc = campo "Modelo" do cadastro da natureza (frota.tnocad) — mesma origem de
// verdade usada em NaturezasOperacao.tsx (OPCOES_MODELO). Não é derivado do CFOP.
const MODELO_LABEL: Record<string, { label: string, cls: string }> = {
  E: { label: 'Estadual',       cls: 'badge-cfop badge-cfop--5' },
  M: { label: 'Municipal',      cls: 'badge-cfop badge-cfop--7' },
  U: { label: 'Intermunicipal', cls: 'badge-cfop badge-cfop--6' },
  S: { label: 'Interestadual',  cls: 'badge-cfop badge-cfop--6' },
  I: { label: 'Internacional',  cls: 'badge-cfop badge-cfop--7' },
}

function badgeCfop(tnoLoc?: string) {
  return MODELO_LABEL[(tnoLoc ?? '').trim().toUpperCase()] ?? { label: 'Outro', cls: 'badge-cfop badge-cfop--x' }
}

export default function NaturezaSelect({ naturezas, value, onChange }: Props) {
  const [aberta, setAberta] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [localCod, setLocalCod] = useState(value)
  const [aviso, setAviso] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const { posicao, listaRef } = useListaFlutuante(ref, aberta, () => setAberta(false))

  useEffect(() => { setLocalCod(value); if (value) setAviso('') }, [value])

  const selecionada = naturezas.find(n => String(n.tno_cod) === value)

  const lista = naturezas.filter(n => {
    if (!filtro) return true
    const f = filtro.toLowerCase()
    return (
      String(n.tno_cod).includes(f) ||
      String(n.tno_cdf).includes(f) ||
      n.tno_dsc.toLowerCase().includes(f)
    )
  })

  function selecionar(n: Natureza) {
    onChange(String(n.tno_cod))
    setLocalCod(String(n.tno_cod))
    setAviso('')
    setAberta(false)
    setFiltro('')
  }

  function handleCodBlur() {
    if (!localCod) { onChange(''); setAviso(''); return }
    const encontrada = naturezas.find(n => String(n.tno_cod) === localCod)
    if (encontrada) {
      onChange(localCod)
      setAviso('')
      setAberta(false)
    } else {
      onChange('')
      setAviso(`Código "${localCod}" não encontrado`)
    }
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div className="nat-input-row">
        <input
          className="nat-cod-input"
          type="text"
          placeholder="Cód."
          value={localCod}
          style={aviso ? { borderColor: '#dc2626' } : undefined}
          onChange={e => { setLocalCod(e.target.value.replace(/\D/g, '')); setAviso('') }}
          onBlur={handleCodBlur}
          onKeyDown={e => { if (e.key === 'Enter') handleCodBlur() }}
        />
        <div className="nat-desc-box" onClick={() => setAberta(o => !o)}>
          {selecionada ? (
            <>
              <span className={badgeCfop(selecionada.tno_loc).cls}>{badgeCfop(selecionada.tno_loc).label}</span>
              <span className="nat-cfop">{selecionada.tno_cdf}</span>
              <span className="nat-dsc">{selecionada.tno_dsc}</span>
            </>
          ) : (
            <span className="nat-placeholder">Selecione ou digite o código...</span>
          )}
          <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
        </div>
      </div>
      {aviso && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.2rem', paddingLeft: '0.25rem' }}>{aviso}</div>}

      {aberta && posicao && createPortal(
        <div ref={listaRef} className="nat-lista" style={{ position: 'fixed', ...posicao, minWidth: posicao.width }}>
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Buscar por código, CFOP ou descrição..."
              value={filtro}
              onChange={e => setFiltro(e.target.value)}
            />
          </div>
          <div className="nat-itens">
            {lista.length === 0 && <div className="nat-vazio">Nenhum resultado</div>}
            {lista.map(n => {
              const b = badgeCfop(n.tno_loc)
              return (
                <button
                  key={n.tno_cod}
                  className={`nat-item${String(n.tno_cod) === value ? ' nat-item--ativo' : ''}`}
                  onClick={() => selecionar(n)}
                >
                  <div className="nat-item-linha1">
                    <span className="nat-item-cod">#{n.tno_cod}</span>
                    <span className="nat-item-cfop">CFOP {n.tno_cdf}</span>
                    <span className={b.cls}>{b.label}</span>
                  </div>
                  <div className="nat-item-dsc">{n.tno_dsc}</div>
                </button>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
